import { useReducer } from 'react';
import type { ProgramFilteringRule, ProgramSortingRule } from '@/apolloClient.types';
import { ProgramSortingField as DefaultSortField, SortingOrder as GqlSortingOrder } from '@/apolloClient.types';
import type { SortOrder as TableSortOrderValue } from '@/components/common/Table/types';
import { SortingOrder as TableSortingOrder } from '@/components/common/Table/types';

// Define the shape of the sort state, including the table's display value
type SortRule = ProgramSortingRule & { orderValue: TableSortOrderValue | undefined };

// Define the state shape for the course instance list
export interface State {
    page: number;
    pageSize: number;
    sort: SortRule;
    filter: ProgramFilteringRule;
    selectedCompanyId: string | null;
}

// Define Action Types
export interface SetFilterAction {
    type: 'setFilter';
    filter: ProgramFilteringRule;
}
export interface SetSortAction {
    type: 'setSort';
    sortBy: SortRule;
}
export interface SetPageAction {
    type: 'setPage';
    page: number;
}
export interface SetPageSizeAction {
    type: 'setPageSize';
    pageSize: number;
}
export interface SetCompanyAction {
    type: 'setCompany';
    companyId: string | null;
}

export type Action = SetFilterAction | SetSortAction | SetPageAction | SetPageSizeAction | SetCompanyAction;

// The reducer function
const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'setFilter':
            // Reset to page 1 when filter changes
            return { ...state, page: 1, filter: action.filter };

        case 'setSort':
            // Reset to page 1 when sort changes
            return { ...state, page: 1, sort: action.sortBy };

        case 'setPage':
            return { ...state, page: action.page };

        case 'setPageSize':
            // Reset to page 1 when page size changes
            return { ...state, page: 1, pageSize: action.pageSize };

        case 'setCompany':
            // Reset to page 1 when company changes
            return { ...state, page: 1, selectedCompanyId: action.companyId };

        default:
            return state;
    }
};

const initialState: State = {
    page: 1,
    pageSize: 10,
    sort: {
        field: DefaultSortField.Name,
        order: GqlSortingOrder.Asc,
        orderValue: TableSortingOrder.Asc,
    },
    filter: {},
    selectedCompanyId: null,
};

const useProgramListReducer = () => useReducer(reducer, initialState);

export default useProgramListReducer;
